/**
 * useFabState.js
 *
 * Возвращает { hidden } — прятать ли плавающую «+».
 *
 * ДВА КОНТУРА, и главный из них — второй:
 *
 *  1. События `fab-hide` / `fab-show` — ручной опт-ин. Оставлены для случаев,
 *     которые по DOM не видны (страница сама решила спрятать кнопку), и для
 *     обратной совместимости с уже размеченными местами.
 *
 *  2. Автодетект оверлеев по DOM — работает без единой строчки в модале.
 *     Ручная разметка тут проиграла по факту: полноэкранных слоёв в проекте
 *     около сотни в 45 файлах, событие слали пятнадцать. Модал выбора шаблона
 *     отчёта слал `fab-hide` только на превью PDF, а на саму шторку — нет, и
 *     кнопка висела поверх. Опт-ин, о котором нужно помнить в каждом новом
 *     модале, забывают — поэтому решение перенесено к самой кнопке: она
 *     смотрит, не накрыл ли её кто-нибудь, и гаснет сама.
 *
 * ⚠️ На порядок отрисовки полагаться нельзя: FAB портируется в body, а модалы
 * часто живут внутри своего stacking context, поэтому z-index 9998 у бэкдропа
 * не гарантирует, что он выше кнопки с z-index 1000. Считаем геометрию.
 *
 * Чтобы исключить элемент из детекта — `data-fab-ignore` на нём или на предке
 * (шелл приложения, собственный портал кнопки).
 *
 * Ручное управление из любого места:
 *   window.dispatchEvent(new CustomEvent('fab-hide'))
 *   window.dispatchEvent(new CustomEvent('fab-show'))
 */
import { useEffect, useState } from 'react';

/**
 * Кандидаты в оверлеи. Дешёвый отбор по атрибуту/классу, а не обход всего
 * дерева: getComputedStyle стоит рекалка стилей, гонять её по тысячам узлов
 * на каждую мутацию нельзя.
 *
 * `[style*="fixed"]` ловит инлайновые стили React — доминирующий паттерн в
 * проекте. Классовые оверлеи из index.css перечислены явно.
 */
const OVERLAY_SELECTOR = [
  '[style*="fixed"]',
  '.modal-overlay',
  '.notif-overlay',
  '.notif-drawer',
  '[data-overlay]',
].join(',');

/** Доля вьюпорта, с которой слой считается полноэкранным бэкдропом. */
const FULLSCREEN_RATIO = 0.9;
/** Доля площади вьюпорта, с которой панель, накрывшая кнопку, считается оверлеем. */
const PANEL_AREA_RATIO = 0.2;

/**
 * Чистое правило «этот слой накрыл кнопку» — вынесено из работы с DOM, чтобы
 * его можно было прогнать без браузера.
 *
 * @param {object} cs   — снимок computed style (position/display/visibility/
 *                        pointerEvents/opacity)
 * @param {object} r    — getBoundingClientRect слоя
 * @param {object|null} fabRect — прямоугольник самой кнопки
 */
export function isCoveringOverlay(cs, r, vw, vh, fabRect) {
  if (cs.position !== 'fixed') return false;
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  // Декоративные слои (подсветка гайда, контейнер тостов) кликов не ловят —
  // кнопку они не закрывают, гасить её из-за них незачем.
  if (cs.pointerEvents === 'none') return false;
  if (parseFloat(cs.opacity ?? '1') < 0.05) return false;

  // Видимая часть, а не весь прямоугольник: ящик уведомлений и мобильное меню
  // висят в DOM постоянно и уезжают за экран трансформом — по «сырому» размеру
  // они выглядели бы как полноэкранный слой.
  const left   = Math.max(0,  r.left);
  const top    = Math.max(0,  r.top);
  const width  = Math.min(vw, r.right)  - left;
  const height = Math.min(vh, r.bottom) - top;
  if (width <= 0 || height <= 0) return false;

  // R1 — полноэкранный бэкдроп. Сама шторка/диалог лежит внутри него, поэтому
  // ловить достаточно бэкдроп.
  if (width >= vw * FULLSCREEN_RATIO && height >= vh * FULLSCREEN_RATIO) return true;

  // R2 — панель без бэкдропа, накрывшая саму кнопку (боковой ящик, шит).
  if (fabRect && width * height >= vw * vh * PANEL_AREA_RATIO) {
    const hit = left < fabRect.right && left + width  > fabRect.left
             && top  < fabRect.bottom && top  + height > fabRect.top;
    if (hit) return true;
  }

  return false;
}

/**
 * ⚠️ Две РАЗНЫЕ метки, и путать их нельзя:
 *
 *   data-fab-root   — портал самой кнопки. Скрываем узел И ВСЁ ВНУТРИ: своё
 *                     затемнение меню — такой же `fixed; inset:0`.
 *   data-fab-ignore — ровно этот узел, без потомков. Носит его шелл
 *                     `.app-layout`. Через `closest()` его проверять НЕЛЬЗЯ:
 *                     модалы живут внутри шелла, а не в портале, и предок с
 *                     меткой отключал бы детект для всего приложения.
 */
function isOverlayNode(el, vw, vh, fabRect) {
  if (el.hasAttribute('data-fab-ignore')) return false;
  if (el.closest('[data-fab-root]')) return false;
  return isCoveringOverlay(
    window.getComputedStyle(el),
    el.getBoundingClientRect(),
    vw, vh, fabRect,
  );
}

/**
 * Есть ли сейчас на экране слой, накрывший кнопку. Экспортируется, чтобы
 * проверку можно было прогнать на живом DOM без React — первая версия этой
 * фичи прошла тест чистого предиката и всё равно не работала: ломалась именно
 * проводка (исключение по предку гасило детект во всём приложении).
 */
export function findCoveringOverlay(vw, vh, fabRect) {
  const nodes = document.querySelectorAll(OVERLAY_SELECTOR);
  for (let i = 0; i < nodes.length; i += 1) {
    if (isOverlayNode(nodes[i], vw, vh, fabRect)) return nodes[i];
  }
  return null;
}

/**
 * @param {React.RefObject<HTMLElement>} fabRef — ссылка на саму кнопку «+»
 *   (не на корень портала: при раскрытом меню корень включает бэкдроп на весь
 *   экран, и правило R2 сработало бы на что угодно).
 */
export function useFabState(fabRef = null) {
  /**
   * СЧЁТЧИК, а не флаг. Слои вкладываются друг в друга: Работа (TaskModal) →
   * Сметы Работы → редактор сметы. Каждый шлёт свою пару hide/show, и при
   * булевом флаге закрытие ВНУТРЕННЕГО слоя «показывало» FAB поверх ещё
   * открытого внешнего.
   */
  const [hideCount, setHideCount] = useState(0);
  // Отдельный замок для демо-гайда — не конфликтует с lightbox-open/close,
  // чтобы закрытие фото посреди рельсов не «показало» FAB обратно.
  const [demoLock, setDemoLock] = useState(false);
  const [domHidden, setDomHidden] = useState(false);

  useEffect(() => {
    const onHide = () => setHideCount(c => c + 1);
    const onShow = () => setHideCount(c => Math.max(0, c - 1));
    const onLock = () => setDemoLock(true);
    const onUnlock = () => setDemoLock(false);

    window.addEventListener('fab-hide',       onHide);
    window.addEventListener('fab-show',       onShow);
    // обратная совместимость с FullscreenViewer
    window.addEventListener('lightbox-open',  onHide);
    window.addEventListener('lightbox-close', onShow);
    window.addEventListener('demo-fab-lock',   onLock);
    window.addEventListener('demo-fab-unlock', onUnlock);

    return () => {
      window.removeEventListener('fab-hide',       onHide);
      window.removeEventListener('fab-show',       onShow);
      window.removeEventListener('lightbox-open',  onHide);
      window.removeEventListener('lightbox-close', onShow);
      window.removeEventListener('demo-fab-lock',   onLock);
      window.removeEventListener('demo-fab-unlock', onUnlock);
    };
  }, []);

  // ─── Автодетект ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;

    let frame = 0;
    let trailing = 0;
    let alive = true;

    const scan = () => {
      frame = 0;
      if (!alive) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Кнопка спрятана через opacity, а не unmount — прямоугольник у неё
      // остаётся честным и в скрытом состоянии.
      const fabRect = fabRef?.current?.getBoundingClientRect() || null;
      setDomHidden(!!findCoveringOverlay(vw, vh, fabRect));
    };

    // Скан в rAF: мутаций много (ввод в поля, ре-рендеры списков), а ответ
    // нужен один на кадр. Заодно попадаем в момент после лэйаута.
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(scan);
      // Догоняющий скан: слой, который въезжает через CSS-переход
      // (opacity 0 → 1), в момент монтирования ещё прозрачный, а второй
      // мутации у него не будет — без повторной проверки кнопка осталась бы
      // висеть поверх уже открытого модала.
      if (trailing) window.clearTimeout(trailing);
      trailing = window.setTimeout(() => { trailing = 0; scan(); }, 260);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList:      true,
      subtree:        true,
      attributes:     true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    schedule();

    return () => {
      alive = false;
      if (frame) window.cancelAnimationFrame(frame);
      if (trailing) window.clearTimeout(trailing);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
  }, [fabRef]);

  return { hidden: hideCount > 0 || demoLock || domHidden };
}
